import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Gavel, ArrowLeft, Loader2, Plus, Sparkles, Globe, Lock, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DecreeList } from "@/components/DecreeList";

const SUGGESTIONS = [
  "Healing", "Provision", "Protection", "Peace", "Marriage", "Children",
  "Breakthrough", "Favor", "Identity", "Anxiety", "Fear", "Finances",
];

export default function Generator() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState("topic"); // topic | self | who | name | result
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("self");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [related, setRelated] = useState([]);
  const [session, setSession] = useState(null);
  const [seed, setSeed] = useState(null); // { reference, text } from "Decree this verse"
  const [focusWord, setFocusWord] = useState(null); // a specific word from the seed verse
  const [visibility, setVisibility] = useState("private"); // private | public
  const [anonymous, setAnonymous] = useState(false);
  const [posterName, setPosterName] = useState("");
  const [postedPublic, setPostedPublic] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [planDays, setPlanDays] = useState(7);
  const [guestUsed, setGuestUsed] = useState(() => Number(sessionStorage.getItem("iie_guest_gen") || 0));

  const focusChoices = seed
    ? Array.from(
        new Set(
          seed.text
            .replace(/[^a-zA-Z\s]/g, " ")
            .split(/\s+/)
            .filter((w) => w.length > 3)
            .map((w) => w.toLowerCase())
        )
      ).slice(0, 12)
    : [];

  useEffect(() => {
    const sv = location.state?.seedVerse;
    if (sv?.reference) {
      setSeed(sv);
      setTopic(sv.reference);
      setStep("self");
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const inputCls =
    "w-full bg-transparent border border-[#D9D4CC] px-4 py-3.5 text-lg text-[#2C2A29] placeholder-[#A39C90] rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none transition-all duration-300";

  const toggleCls = (active) =>
    `flex items-center gap-2 px-4 py-2.5 text-sm rounded-sm border transition-colors duration-300 ${
      active ? "bg-[#2C2A29] text-[#FAF9F6] border-[#2C2A29]" : "border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF]"
    }`;

  const generate = async (aud, who) => {
    setLoading(true);
    setStep("result");
    const isPublic = user && visibility === "public";
    try {
      const { data } = await api.post("/decrees/generate", {
        topic,
        audience: aud,
        name: who || null,
        seed_verse: seed ? `${seed.reference}: ${seed.text}` : undefined,
        focus_word: focusWord || undefined,
        visibility: isPublic ? "public" : "private",
        anonymous: isPublic ? anonymous : false,
        poster_name: isPublic && !anonymous ? posterName.trim() || undefined : undefined,
      });
      setSession(data);
      setRelated(data.related_topics || []);
      setPostedPublic(!!isPublic);
      if (!user) {
        const n = Number(sessionStorage.getItem("iie_guest_gen") || 0) + 1;
        sessionStorage.setItem("iie_guest_gen", String(n));
        setGuestUsed(n);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate decrees.");
      setStep("topic");
    } finally {
      setLoading(false);
    }
  };

  const appendMore = async (newTopic) => {
    if (!session) return;
    setLoadingMore(true);
    try {
      const { data } = await api.post("/decrees/generate", {
        topic: newTopic || session.topic,
        audience,
        name: name || null,
        exclude: session.decrees.map((d) => d.reference),
        session_id: session.id || undefined,
        seed_verse: !newTopic && seed ? `${seed.reference}: ${seed.text}` : undefined,
      });
      setSession((prev) => ({ ...prev, decrees: [...prev.decrees, ...data.decrees] }));
      if (data.related_topics?.length) {
        setRelated(data.related_topics.filter((t) => t.toLowerCase() !== (newTopic || "").toLowerCase()));
      }
      toast.success(newTopic ? `Added decrees for ${newTopic}` : "More decrees added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load more decrees.");
    } finally {
      setLoadingMore(false);
    }
  };

  const reset = () => {
    setStep("topic");
    setTopic("");
    setAudience("self");
    setName("");
    setRelated([]);
    setSession(null);
    setSeed(null);
    setFocusWord(null);
    setVisibility("private");
    setAnonymous(false);
    setPosterName("");
    setPostedPublic(false);
  };

  const createPlan = async () => {
    if (!session) return;
    setCreatingPlan(true);
    try {
      const { data } = await api.post("/plans", {
        mode: "theme",
        theme: session.topic,
        per_day: 5,
        days: planDays,
      });
      toast.success("Your decree plan is ready!");
      navigate(`/plans/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create plan.");
    } finally {
      setCreatingPlan(false);
    }
  };

  const choiceBtn =
    "w-full text-left px-6 py-5 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm hover:bg-[#EAE6DF] hover:border-[#B7AE9F] transition-all duration-300 font-decree text-xl text-[#2C2A29]";

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24">
      {/* STEP: TOPIC */}
      {step === "topic" && (
        <div className="animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <Gavel className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">It Is Established</p>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29] tracking-tight leading-tight">
            What would you like to decree today?
          </h1>
          <p className="font-decree text-xl text-[#4A4744] mt-5 leading-relaxed">
            Enter a topic, situation, or Bible keyword below, and I will help you build powerful,
            personalized declarations rooted in God's Word.
          </p>

          {!user && guestUsed >= 1 ? (
            <div data-testid="guest-limit-gate" className="mt-10 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-7">
              <p className="font-heading text-2xl text-[#2C2A29]">You've used your guest decree for this visit</p>
              <p className="font-decree text-lg text-[#4A4744] mt-3 leading-relaxed">
                Create a free account to keep decreeing — full sets of 10–15 decrees, listening, saved history, plans, and more.
              </p>
              <Link
                to="/auth"
                data-testid="guest-limit-signin"
                className="inline-block mt-6 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300"
              >
                Sign in free
              </Link>
            </div>
          ) : (
          <form
            className="mt-10"
            onSubmit={(e) => { e.preventDefault(); if (topic.trim()) setStep("self"); }}
          >
            <input
              data-testid="topic-input"
              autoFocus
              className={inputCls}
              placeholder="e.g. healing, provision, peace…"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <div className="flex flex-wrap gap-2 mt-5">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`suggestion-${s.toLowerCase()}`}
                  onClick={() => setTopic(s)}
                  className="px-3.5 py-1.5 text-sm border border-[#D9D4CC] text-[#4A4744] rounded-full hover:bg-[#EAE6DF] transition-colors duration-300"
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              data-testid="topic-continue-button"
              type="submit"
              disabled={!topic.trim()}
              className="mt-10 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-40"
            >
              Continue
            </button>
          </form>
          )}
        </div>
      )}

      {/* STEP: IS THIS FOR YOURSELF */}
      {step === "self" && (
        <div className="animate-fade-up">
          <StepBack onClick={() => setStep("topic")} label={topic} />
          {seed && (
            <div data-testid="seed-verse-card" className="mt-6 border-l-2 border-[#9B7E4E] bg-[#FDFBF7] rounded-sm px-5 py-4">
              <p className="font-body text-xs uppercase tracking-[0.15em] text-[#9B7E4E]">{seed.reference}</p>
              <p className="font-decree text-lg text-[#2C2A29] leading-relaxed mt-1.5">{seed.text}</p>
            </div>
          )}
          {seed && focusChoices.length > 0 && (
            <div className="mt-6" data-testid="focus-word-section">
              <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">
                Focus on a specific word <span className="normal-case tracking-normal text-[#A39C90]">(optional — or decree the whole verse)</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {focusChoices.map((w) => (
                  <button
                    key={w}
                    type="button"
                    data-testid={`focus-word-${w}`}
                    onClick={() => setFocusWord(focusWord === w ? null : w)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors duration-300 ${
                      focusWord === w
                        ? "bg-[#2C2A29] text-[#FAF9F6] border-[#2C2A29]"
                        : "border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF]"
                    }`}
                  >
                    {w}
                  </button>
                ))}
              </div>
              {focusWord && (
                <p className="font-body text-xs text-[#9B7E4E] mt-2.5" data-testid="focus-word-note">
                  Decrees will center on “{focusWord}”. Tap it again to decree the whole verse.
                </p>
              )}
            </div>
          )}
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#2C2A29] tracking-tight mt-6">
            Is this decree for yourself?
          </h2>
          <div className="space-y-3 mt-8">
            <button data-testid="self-yes" className={choiceBtn} onClick={() => { setAudience("self"); generate("self", null); }}>
              Yes — these decrees are for me
            </button>
            <button data-testid="self-no" className={choiceBtn} onClick={() => setStep("who")}>
              No — for someone else
            </button>
          </div>

          {user && (
            <div className="mt-10 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-5" data-testid="visibility-section">
              <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Sharing</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="visibility-private" onClick={() => setVisibility("private")} className={toggleCls(visibility === "private")}>
                  <Lock className="w-4 h-4" /> Keep private
                </button>
                <button type="button" data-testid="visibility-public" onClick={() => setVisibility("public")} className={toggleCls(visibility === "public")}>
                  <Globe className="w-4 h-4" /> Post to Newsfeed
                </button>
              </div>
              {visibility === "public" && (
                <div className="mt-4 space-y-3" data-testid="public-options">
                  <label className="flex items-center gap-2.5 text-sm text-[#4A4744]">
                    <input
                      type="checkbox"
                      data-testid="anonymous-checkbox"
                      checked={anonymous}
                      onChange={(e) => setAnonymous(e.target.checked)}
                      className="w-4 h-4 accent-[#2C2A29]"
                    />
                    Post anonymously
                  </label>
                  {!anonymous && (
                    <input
                      data-testid="poster-name-input"
                      value={posterName}
                      onChange={(e) => setPosterName(e.target.value)}
                      placeholder={`Display name (defaults to ${user.name || "your name"})`}
                      className="w-full bg-transparent border border-[#D9D4CC] px-4 py-2.5 text-base rounded-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none"
                    />
                  )}
                  <p className="font-body text-xs text-[#9B7E4E]">
                    Public decrees are capped at 7 and shared on the community Newsfeed.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* STEP: SINGLE OR GROUP */}
      {step === "who" && (
        <div className="animate-fade-up">
          <StepBack onClick={() => setStep("self")} label={topic} />
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#2C2A29] tracking-tight mt-6">
            Is this for a single person or a group?
          </h2>
          <div className="space-y-3 mt-8">
            <button data-testid="who-person" className={choiceBtn} onClick={() => { setAudience("person"); setStep("name"); }}>
              A single person
            </button>
            <button data-testid="who-group" className={choiceBtn} onClick={() => { setAudience("group"); setStep("name"); }}>
              A group of people
            </button>
          </div>
        </div>
      )}

      {/* STEP: NAME / DESCRIPTION */}
      {step === "name" && (
        <div className="animate-fade-up">
          <StepBack onClick={() => setStep("who")} label={topic} />
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#2C2A29] tracking-tight mt-6">
            {audience === "person" ? "What is their name?" : "How would you describe this group?"}
          </h2>
          <p className="font-decree text-lg text-[#7A756E] mt-3">
            {audience === "person"
              ? "Your decrees will be declared over them by name."
              : "e.g. my family, my church, my team, my children"}
          </p>
          <form
            className="mt-8"
            onSubmit={(e) => { e.preventDefault(); if (name.trim()) generate(audience, name.trim()); }}
          >
            <input
              data-testid="name-input-field"
              autoFocus
              className={inputCls}
              placeholder={audience === "person" ? "Their name" : "Describe the group"}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <button
              data-testid="name-continue-button"
              type="submit"
              disabled={!name.trim()}
              className="mt-8 px-8 py-3.5 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-40"
            >
              Generate decrees
            </button>
          </form>
        </div>
      )}

      {/* STEP: RESULT */}
      {step === "result" && (
        <div>
          {loading ? (
            <div data-testid="generating-state" className="text-center py-24 animate-fade-up">
              <Loader2 className="w-8 h-8 text-[#2C2A29] animate-spin mx-auto" />
              <p className="font-decree text-xl text-[#4A4744] mt-6">
                Searching the Scriptures and establishing your decrees…
              </p>
            </div>
          ) : session ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">Decrees for</p>
                  <h1 className="font-heading text-2xl sm:text-3xl text-[#2C2A29] tracking-tight capitalize mt-1">
                    {session.topic}
                  </h1>
                </div>
                <button
                  data-testid="new-decree-button"
                  onClick={reset}
                  className="px-5 py-2.5 text-sm border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300"
                >
                  New decree
                </button>
              </div>
              {!user && (
                <div
                  data-testid="guest-save-banner"
                  className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm px-5 py-4"
                >
                  <p className="font-body text-sm text-[#4A4744]">
                    You're decreeing as a guest. Sign in to save these to your history.
                  </p>
                  <Link
                    to="/auth"
                    data-testid="guest-signin-link"
                    className="shrink-0 px-4 py-2 text-sm bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 text-center"
                  >
                    Sign in to save
                  </Link>
                </div>
              )}
              <DecreeList session={session} />

              {postedPublic && (
                <div data-testid="posted-public-note" className="mt-6 flex items-center gap-2 text-sm text-[#9B7E4E]">
                  <Globe className="w-4 h-4" />
                  <span>Shared to the community Newsfeed.</span>
                  <Link to="/feed" data-testid="posted-public-view" className="underline underline-offset-2 hover:text-[#2C2A29] transition-colors duration-300">View</Link>
                </div>
              )}

              {user ? (
              <div className="mt-12 pt-8 border-t border-[#EAE6DF] animate-fade-up">
                <button
                  data-testid="load-more-button"
                  onClick={() => appendMore()}
                  disabled={loadingMore}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300 disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {loadingMore ? "Searching the Scriptures…" : "Load more decrees"}
                </button>

                {related.length > 0 && (
                  <div data-testid="related-topics" className="mt-10">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-[#B7AE9F]" strokeWidth={1.75} />
                      <p className="font-body text-sm uppercase tracking-[0.15em] text-[#7A756E]">
                        Add complementary decrees
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {related.map((t) => (
                        <button
                          key={t}
                          data-testid={`related-topic-${t.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => appendMore(t)}
                          disabled={loadingMore}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm border border-[#D9D4CC] text-[#4A4744] rounded-full hover:bg-[#EAE6DF] hover:text-[#2C2A29] transition-colors duration-300 disabled:opacity-50 capitalize"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              ) : (
                <div data-testid="guest-more-gate" className="mt-12 pt-8 border-t border-[#EAE6DF] animate-fade-up">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm px-5 py-4">
                    <p className="font-body text-sm text-[#4A4744]">
                      Sign in free to load more decrees and add complementary topics.
                    </p>
                    <Link
                      to="/auth"
                      data-testid="guest-more-signin"
                      className="shrink-0 px-4 py-2 text-sm bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 text-center"
                    >
                      Sign in free
                    </Link>
                  </div>
                </div>
              )}

              {user && (
                <div className="mt-10 pt-8 border-t border-[#EAE6DF] animate-fade-up" data-testid="create-plan-section">
                  <div className="flex items-center gap-2 mb-3">
                    <CalendarPlus className="w-4 h-4 text-[#B7AE9F]" strokeWidth={1.75} />
                    <p className="font-body text-sm uppercase tracking-[0.15em] text-[#7A756E]">Build a habit</p>
                  </div>
                  <p className="font-decree text-lg text-[#4A4744] mb-4 capitalize">
                    Turn “{session.topic}” into a daily decree plan and receive a fresh set each day.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="text-sm text-[#4A4744] flex items-center gap-2">
                      Length
                      <select
                        data-testid="plan-days-select"
                        value={planDays}
                        onChange={(e) => setPlanDays(Number(e.target.value))}
                        className="border border-[#D9D4CC] bg-transparent rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#EAE6DF] focus:border-[#B7AE9F] focus:outline-none"
                      >
                        {[3, 7, 14, 21, 30, 40].map((d) => (
                          <option key={d} value={d}>{d} days{user?.tier === "free" && d > 3 ? " — Premium" : ""}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      data-testid="create-plan-button"
                      onClick={createPlan}
                      disabled={creatingPlan}
                      className="flex items-center gap-2 px-6 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
                    >
                      {creatingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                      {creatingPlan ? "Building your plan…" : "Create decree plan"}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function StepBack({ onClick, label }) {
  return (
    <button
      data-testid="step-back-button"
      onClick={onClick}
      className="flex items-center gap-2 text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300"
    >
      <ArrowLeft className="w-4 h-4" />
      <span className="capitalize">{label}</span>
    </button>
  );
}
